import { useEffect, useState } from 'react';
import { listCaptures, type CaptureListEntry } from '../api-captures';
import { absoluteUrl } from '../api';
import { useStore } from '../store';

function notify(
  captureId: string,
  payload: { method: string; path: string; body: string | null; query: string | null },
): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    new Notification(`xss.cool: ${payload.method} on /c/${captureId.slice(0, 8)}…`, {
      body: payload.body
        ? payload.body.slice(0, 80)
        : `${payload.path}${payload.query ? '?' + payload.query : ''}`,
      tag: `xsscool-capture-${captureId}-${Date.now()}`,
    });
  } catch {
    /* ignore */
  }
}

/**
 * Always-mounted component that subscribes to SSE for every owned capture and
 * bumps the unread counter on new requests. Independent of CapturesPanel so
 * the badge updates regardless of which tab is visible.
 */
export function CaptureBadgeListener() {
  const bumpUnread = useStore((s) => s.bumpUnreadCaptures);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onChange = () => setTick((t) => t + 1);
    window.addEventListener('xsscool:captures-changed', onChange);
    return () => window.removeEventListener('xsscool:captures-changed', onChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const sources: EventSource[] = [];

    void listCaptures().then((captures: CaptureListEntry[]) => {
      if (cancelled) return;
      for (const c of captures) {
        const url = absoluteUrl(`/c/${c.id}/stream`);
        const source = new EventSource(url, { withCredentials: true });
        source.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data) as {
              method: string;
              path: string;
              body: string | null;
              query: string | null;
            };
            const state = useStore.getState();
            if (state.tab !== 'captures') {
              bumpUnread();
              if (state.notifyOnCapture) notify(c.id, payload);
            }
          } catch {
            /* ignore */
          }
        };
        sources.push(source);
      }
    });

    return () => {
      cancelled = true;
      for (const s of sources) s.close();
    };
  }, [tick, bumpUnread]);

  return null;
}
